import type { IChallenge } from "@bbj-types/challenge";
import { useState } from "react";
import type { TPaperCandidateChallengeAction } from "./actions/types";
import type { IPaperCandidateChallengeProps } from "./index";
import type { TPaperCandidateChallengeChallenge, TPaperCandidateChallengeObjective } from "./types";

export enum CandidateChallengeLoadingState {
  NONE = 0,
  DELETE_CHALLENGE = 1,
  CREATE_OBJECTIVE = 2,
  EDIT_OBJECTIVE = 3,
  DELETE_OBJECTIVE = 4,
  EDIT_CHALLENGE = 5,
}

export const usePaperCandidateChallenge = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
  Action extends TPaperCandidateChallengeAction,
>(
  challenges: IChallenge[],
  candidateChallenge: CandidateChallenge,
  {
    onDeleteChallenge,
    onEditChallenge,
    onCreateObjective,
    onEditObjective,
    onDeleteObjective,
  }: Pick<
    IPaperCandidateChallengeProps<CandidateObjective, CandidateChallenge, Action>,
    "onDeleteChallenge" | "onEditChallenge" | "onCreateObjective" | "onEditObjective" | "onDeleteObjective"
  >
) => {
  const [loading, setLoading] = useState(CandidateChallengeLoadingState.NONE);
  const [isEditMode, setIsEditMode] = useState(false);
  const currentChallenge = challenges.find((challenge) => challenge._id === candidateChallenge.challenge);
  const createdFromCer =
    candidateChallenge.createdFromCer || candidateChallenge.objectives.some((obj) => obj.createdFromCer);

  const handleDeleteChallenge = async () => {
    if (!onDeleteChallenge) {
      return;
    }
    setLoading(CandidateChallengeLoadingState.DELETE_CHALLENGE);
    try {
      await onDeleteChallenge(candidateChallenge);
    } finally {
      setLoading(CandidateChallengeLoadingState.NONE);
    }
  };

  const handleEditChallenge = async (editedCandidateChallenge: CandidateChallenge) => {
    if (!onEditChallenge) {
      return;
    }
    setLoading(CandidateChallengeLoadingState.EDIT_CHALLENGE);
    try {
      await onEditChallenge(editedCandidateChallenge);
      setIsEditMode(false);
    } finally {
      setLoading(CandidateChallengeLoadingState.NONE);
    }
  };

  const handleCreatObjective = async (candidateObjective: TPaperCandidateChallengeObjective) => {
    if (!onCreateObjective) {
      return;
    }
    setLoading(CandidateChallengeLoadingState.CREATE_OBJECTIVE);
    try {
      await onCreateObjective(candidateObjective);
    } finally {
      setLoading(CandidateChallengeLoadingState.NONE);
    }
  };

  const handleEditObjective = async (candidateObjective: CandidateObjective, index: number) => {
    if (!onEditObjective) {
      return;
    }
    setLoading(CandidateChallengeLoadingState.EDIT_OBJECTIVE);
    try {
      await onEditObjective(candidateObjective, index);
    } finally {
      setLoading(CandidateChallengeLoadingState.NONE);
    }
  };

  const handleDeleteObjective = async (candidateObjective: CandidateObjective, index: number) => {
    if (!onDeleteObjective) {
      return;
    }
    setLoading(CandidateChallengeLoadingState.DELETE_OBJECTIVE);
    try {
      await onDeleteObjective(candidateObjective, index);
    } finally {
      setLoading(CandidateChallengeLoadingState.NONE);
    }
  };

  return {
    currentChallenge,
    loading,
    createdFromCer,
    isEditMode,
    setIsEditMode,
    handleDeleteChallenge,
    handleEditChallenge,
    handleCreatObjective,
    handleEditObjective,
    handleDeleteObjective,
  };
};
